import React, { useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListSubheader from "@material-ui/core/ListSubheader";
import api from "../../store/api";
import { differenceInMonths, format } from "date-fns";
import DateFnsUtils from "@date-io/date-fns";
import { RepeatIcon } from "../../assets/iconComponents/Icons";
import MedicationDetails from "./MedicationDetails";
import _ from "lodash";
import clsx from "clsx";

import activitySvg from "../../assets/svgs/activity.svg"
import ambulantSvg from "../../assets/svgs/ambulant.svg"
import acuityBSvg from "../../assets/svgs/acuityB.svg"
import cardiacMonitoringSvg from "../../assets/svgs/cardiacMonitoring.svg"
import infectionSvg from "../../assets/svgs/infection.svg"
import InfectionRiskSvg from "../../assets/svgs/InfectionRisk.svg"
import ivInfusionIconSvg from "../../assets/svgs/ivInfusionIcon.svg"
import medicationSvg from "../../assets/svgs/medication.svg"
import memoryLossIconSvg from "../../assets/svgs/memoryLossIcon.svg"
import nilByMouthIconSvg from "../../assets/svgs/nilByMouthIcon.svg"
import nonambulantSvg from "../../assets/svgs/nonambulant.svg"
import o2DeliveryIconSvg from "../../assets/svgs/o2DeliveryIcon.svg"
import patientDisplaysAggressionSvg from "../../assets/svgs/patientDisplaysAggression.svg"
import potentiallySvg from "../../assets/svgs/potentially.svg"
import prescribedMedicationSvg from "../../assets/svgs/prescribedMedication.svg"
import sedatedSvg from "../../assets/svgs/sedated.svg"
import mentalHealthSvg from "../../assets/svgs/mentalHealth.svg"
import translatorSvg from "../../assets/svgs/translator.svg"

// DARK ICONS
import activityDarkSvg from "../../assets/svgs/black/activity.svg"
import ambulantDarkSvg from "../../assets/svgs/black/ambulant.svg"
import acuityBDarkSvg from "../../assets/svgs/black/acuityB.svg"
import cardiacMonitoringDarkSvg from "../../assets/svgs/black/cardiacMonitoring.svg"
import infectionDarkSvg from "../../assets/svgs/black/infection.svg"
import InfectionRiskDarkSvg from "../../assets/svgs/black/InfectionRisk.svg"
import ivInfusionIconDarkSvg from "../../assets/svgs/black/ivInfusionIcon.svg"
import medicationDarkSvg from "../../assets/svgs/black/medication.svg"
import memoryLossIconDarkSvg from "../../assets/svgs/black/memoryLossIcon.svg"
import nilByMouthIconDarkSvg from "../../assets/svgs/black/nilByMouthIcon.svg"
import nonambulantDarkSvg from "../../assets/svgs/black/nonambulant.svg"
import o2DeliveryIconDarkSvg from "../../assets/svgs/black/o2DeliveryIcon.svg"
import patientDisplaysAggressionDarkSvg from "../../assets/svgs/black/patientDisplaysAggression.svg"
import potentiallyDarkSvg from "../../assets/svgs/potentially.svg"
import prescribedMedicationDarkSvg from "../../assets/svgs/black/prescribedMedication.svg"
import sedatedDarkSvg from "../../assets/svgs/black/sedated.svg"
import mentalHealthDarkSvg from "../../assets/svgs/black/mentalHealth.svg"
import translatorDarkSvg from "../../assets/svgs/black/translator.svg"

export interface SpecialNeed {
  specialNeedId: string;
  name: string;
  selected: boolean;
  iconUrl: string;
}

interface SelectItem extends React.Props<any> {
  text: string | undefined;
  value: string | undefined;
  selected: boolean;
}

const useStyles = (colorPrimary: string) => {
  return makeStyles((theme: Theme) =>
    createStyles({
      root: {
        flexGrow: 1,
      },
      paper: {
        padding: theme.spacing(0),
        textAlign: "center",
        color: theme.palette.text.secondary,
      },
      listItemTextPrimary: {
        fontSize: 46,
        fontWeight: 700,
        color: colorPrimary,
      },
      listItemTextSecondary: {
        fontSize: 24,
        fontWeight: 700,
        color: colorPrimary,
      },
      needsPoint: {
        fontSize: 19,
        fontWeight: 400,
        color: colorPrimary,
      },
      badge: {
        borderRadius: 0,
      },
      submitButtonRoot: {
        backgroundColor: "#02489D",
        borderRadius: 6,
        padding: "15px 35px",
        "&:hover": {
          backgroundColor: "#002d63",
        },
      },
      submitButtonLabel: {
        textTransform: "capitalize",
        fontSize: 18,
        fontWeight: 400,
        color: "#FFF",
      },
      formControlLabel: {
        fontSize: 18,
        color: "#4A4A4A",
        padding: theme.spacing(0, 0, 1, 0),
      },

      extraInfoContainer: {
        paddingLeft: 16,
        marginTop: 10,
      },
      extraInfoHeader: {
        fontSize: 18,
        fontWeight: 700,
        color: colorPrimary,
        opacity: 0.28,
        marginTop: 23,
      },
      extraInfoText: {
        fontSize: 24,
        fontWeight: "bold",
        color: colorPrimary,
        marginTop: 10,
      },
      extraInfoDateTime: {
        display: "block",
        fontSize: 24,
        fontWeight: 700,
        color: colorPrimary,
        height: "100%",
      },
      medicationsInfo: {
        fontSize: 21,
        fontWeight: 400,
        color: colorPrimary,
        height: "100%",
      },
      medicationsPopupLink: {
        cursor: "pointer",
        textDecoration: "underline",
      },
      cardContainer: {
        marginTop: 10,
        marginBottom: 10,
        boxShadow: "none",
      },
      cardHeader: {
        display: "flex",
        justifyContent: "space-between",

        "@media (max-width: 1024px)": {
          flexDirection: "column",
        },
      },
      primaryText: {
        fontSize: 24,
        fontWeight: 700,
        color: colorPrimary,

        "@media (max-width: 1024px)": {
          fontSize: 18,
        },
      },
      secondaryText: {
        marginTop: -3,
        fontSize: 21,
        fontWeight: 400,
        color: colorPrimary,

        "@media (max-width: 1024px)": {
          fontSize: 16,
        },
      },
      noteText: {
        marginTop: 10,
        fontSize: 18,
        fontWeight: 400,
        color: colorPrimary,

        "@media (max-width: 1024px)": {
          fontSize: 14,
        },
      },
    })
  )();
};

function SimpleCard({ note, heading, primaryText, secondaryText, scheduledDate, colorPrimary, addressBackground }: any) {
  const classes = useStyles(colorPrimary);

  return (
    <Card className={classes.cardContainer} style={{ backgroundColor: addressBackground }}>
      <CardContent>
        <div className={classes.cardHeader}>
          <Typography style={{ fontSize: 18, color: colorPrimary, opacity: 0.28 }} color="textSecondary" gutterBottom>
            {heading}
          </Typography>

          <Typography style={{ fontSize: 18, color: colorPrimary, display: "block" }} color="textSecondary" gutterBottom>
            {scheduledDate}
          </Typography>
        </div>

        <Typography component="div" className={classes.primaryText}>
          {primaryText}
        </Typography>
        <Typography component="div" className={classes.secondaryText}>
          {secondaryText}
        </Typography>

        {note ? (
          <Typography component="div" className={classes.noteText}>
            Note : {note}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

const ICONS_LIGHT: any = {
  "activity.svg": activitySvg,
  "ambulant.svg": ambulantSvg,
  "acuityB.svg": acuityBSvg,
  "cardiacMonitoring.svg": cardiacMonitoringSvg,
  "infection.svg": infectionSvg,
  "InfectionRisk.svg": InfectionRiskSvg,
  "ivInfusionIcon.svg": ivInfusionIconSvg,
  "medication.svg": medicationSvg,
  "memoryLossIcon.svg": memoryLossIconSvg,
  "nilByMouthIcon.svg": nilByMouthIconSvg,
  "nonambulant.svg":nonambulantSvg,
  "o2DeliveryIcon.svg":o2DeliveryIconSvg,
  "patientDisplaysAggression.svg": patientDisplaysAggressionSvg,
  "potentially.svg": potentiallySvg,
  "prescribedMedication.svg": prescribedMedicationSvg,
  "sedated.svg": sedatedSvg,
  "mentalHealth.svg": mentalHealthSvg,
  "translator.svg": translatorSvg,
};

const ICONS_DARK: any = {
  "activity.svg": activityDarkSvg,
  "ambulant.svg": ambulantDarkSvg,
  "acuityB.svg": acuityBDarkSvg,
  "cardiacMonitoring.svg": cardiacMonitoringDarkSvg,
  "infection.svg": infectionDarkSvg,
  "InfectionRisk.svg": InfectionRiskDarkSvg,
  "ivInfusionIcon.svg": ivInfusionIconDarkSvg,
  "medication.svg": medicationDarkSvg,
  "memoryLossIcon.svg": memoryLossIconDarkSvg,
  "nilByMouthIcon.svg": nilByMouthIconDarkSvg,
  "nonambulant.svg":nonambulantDarkSvg,
  "o2DeliveryIcon.svg": o2DeliveryIconDarkSvg,
  "patientDisplaysAggression.svg": patientDisplaysAggressionDarkSvg,
  "potentially.svg": potentiallyDarkSvg,
  "prescribedMedication.svg": prescribedMedicationDarkSvg,
  "sedated.svg": sedatedDarkSvg,
  "mentalHealth.svg": mentalHealthDarkSvg,
  "translator.svg": translatorDarkSvg,
};

const needsStyles = (colorPrimary: string) => {
  return makeStyles((theme: Theme) =>
    createStyles({
      needsPoint: {
        fontSize: 19,
        fontWeight: 400,
        color: colorPrimary,
      },
    })
  )();
};

const NeedListItem = ({ text, iconUrl, colorPrimary, theme }: any) => {
  const classes = needsStyles(colorPrimary);
  let imageName = (iconUrl ? iconUrl.split("/")[iconUrl.split("/").length - 1]?.split(".")[0] : "activity") + ".svg";
  return (
    <ListItem style={{ paddingTop: 0 }}>
      <ListItemIcon style={{ minWidth: 30 }}>
        {iconUrl?.length > 0 && theme == "light" ? <img width={20} height={20} src={ICONS_DARK[imageName]} /> : null}
        {iconUrl?.length > 0 && theme == "dark" ? <img width={20} height={20} src={ICONS_LIGHT[imageName]} /> : null}
      </ListItemIcon>
      <ListItemText style={{ marginLeft: 10 }} classes={{ primary: classes.needsPoint }} primary={text} />
    </ListItem>
  );
};

const AcuityNeedItem = ({ text, colorPrimary, theme }: any) => {
  const acuityMap: any = {
    1: "B",
    2: "C",
    3: "D",
    4: "E",
    5: "H",
    6: "M",
    7: "L",
  };
  let acuity = acuityMap[text] ?? "";

  const classes = needsStyles(colorPrimary);

  return (
    <ListItem style={{ paddingTop: 0 }}>
      <ListItemIcon style={{ minWidth: 30 }}>
        <span style={{ display: "flex", justifyContent: "center", alignItems: "center", width: 20, height: 20, borderRadius: 3, fontSize: 14, color: theme == "dark" ? "#000" : "#fff", background: theme == "dark" ? "#FFF" : "#000" }}>{acuity}</span>
      </ListItemIcon>
      <ListItemText style={{ marginLeft: 10 }} classes={{ primary: classes.needsPoint }} primary={`Acuity ${acuity}`} />
    </ListItem>
  );
};

function _calculateAge(birthday: Date) {
  let months = differenceInMonths(new Date(), birthday);

  return Math.floor(months / 12);
}

const _loadSpecialNeeds = async () => {
  const _res = await api.getSpecialNeeds("");
  if (_res !== null && _res.data !== null && _res.data.model !== null) {
    const specialNeedsMap: any = {};
    _res.data.model.forEach((item: SpecialNeed) => {
      item.selected = false;
      specialNeedsMap[item.specialNeedId] = { name: item.name, icon: item.iconUrl };
    });
    localStorage.setItem("specialNeedsMap", JSON.stringify(specialNeedsMap));
    localStorage.setItem("specialNeeds", JSON.stringify(_res.data.model));
  }
};

type medicationDetails = {
  brandId: string;
  monographId: string;
  monographType: number;
};

function TransferDetailsComponent({ data, theme = "light" }: any) {
    const dateUtil = new DateFnsUtils();
    let colorPrimary = "white",
        addressBackground = "#02489D58",
        medicationColor = "white";
    const isDataExist = data == null || Object.keys(data).length === 0;
    const [fromAddress, setFromAddress] = useState({ primaryText: "", secondaryText: "" });
    const [toAddress, setToAddress] = useState({ primaryText: "", secondaryText: "" });
    const [medicationRoutes, setMedicationRoutes] = useState<SelectItem[]>([]);
    const [medicationUnits, setMedicationUnits] = useState<SelectItem[]>([]);
    const [isSnowMedicationModal, setIsSnowMedicationModal] = useState(false);
    const [modalMedicationType, setModalMedicationType] = useState<medicationDetails | null>(null);
    const [modalMedicationName, setModalMedicationName] = useState("");

    if (theme == "dark") {
        colorPrimary = "white";
        addressBackground = "#02489D58";
        medicationColor = "#F4B827";
    } else {
        colorPrimary = "#333333";
        addressBackground = "#CBCBCB58";
        medicationColor = "black";
    }
    const classes = useStyles(colorPrimary);

    useEffect(() => {
        getMeidcationRoutes();
        getMeidcationUnits();
        setFromToData();
    }, [data]);

    const getMeidcationRoutes = async (): Promise<void> => {
        try {
            const medicationRoutes = localStorage.getItem("medicationRoutes");
            if (medicationRoutes) {
                setMedicationRoutes(JSON.parse(medicationRoutes));
            } else {
                const res = await api.getRoutes("");
                localStorage.setItem("medicationRoutes", JSON.stringify(res.data.model));
                setMedicationRoutes(res.data.model ?? []);
            }
        } catch { }
    };

    const getMeidcationUnits = async (): Promise<void> => {
        try {
            const medicationUnits = localStorage.getItem("medicationUnits");
            if (medicationUnits) {
                setMedicationUnits(JSON.parse(medicationUnits));
            } else {
                const res = await api.getMedicationUnits("");
                localStorage.setItem("medicationUnits", JSON.stringify(res.data.model));
                setMedicationUnits(res.data.model ?? []);
            }
        } catch { }
    };

    const _setFromAddress = async (props: any) => {
        if (props.originGooglePlaceId) {
            setFromAddress((data) => ({ primaryText: props.primaryName, secondaryText: `${props.streetAddress}, ${props.suburb} ${props.state}, ${props.postcode}` }));
        } else if (props.originCustomerLocationId) {
            const locationData = await getLocationsData();
            const primaryText = (locationData != null) ? locationData.find((item: any) => item.value == props.originCustomerLocationId)?.text : "";
            let secondaryText = "";
            if (data.originWardId) {
                const wardsData = await getWardsData(props.originCustomerLocationId);
                const fromWardName = wardsData && wardsData.find((item: any) => item.value == data.originWardId)?.text;
                secondaryText = `${fromWardName ? fromWardName : ""}${data.originBed ? ", Bed " + data.originBed : ""}`;
            } else if (data.originBed) {
                secondaryText = "Bed " + data.originBed;
            }
            setFromAddress((data) => ({ primaryText, secondaryText }));
        }
    };

    const _setToAddress = async (props: any) => {
        if (props.destinationGooglePlaceId) {
            setToAddress((data) => ({ primaryText: props.primaryName, secondaryText: `${props.streetAddress}, ${props.suburb} ${props.state}, ${props.postcode}` }));
        } else if (props.destinationCustomerLocationId) {
            const locationData = await getLocationsData();
            const primaryText = (locationData != null) ? locationData.find((item: any) => item.value == props.destinationCustomerLocationId)?.text : "";
            let secondaryText = "";
            if (data.destinationWardId) {
                const wardsData = await getWardsData(props.destinationCustomerLocationId);
                const toWardName = wardsData && wardsData.find((item: any) => item.value == data.destinationWardId)?.text;
                secondaryText = `${toWardName ? toWardName : ""}${data.destinationBed ? ", Bed " + data.destinationBed : ""}`;
            } else if (data.destinationBed) {
                secondaryText = "Bed " + data.destinationBed;
            }
            setToAddress((data) => ({ primaryText, secondaryText }));
        }
    };

    const getLocationsData = async (): Promise<any> => {
        try {
            const res = await api.getCustomerLocations("");
            return res.data.model;
        } catch {
            //alert("Error getting locations.");
            return null;
        }
    };

    const getWardsData = async (locationId: string): Promise<any> => {
        try {
            const res = await api.getWardsInLocation("locationId=" + locationId);
            return res.data.model;
        } catch { }
    };

    const setFromToData = async () => {
        try {
            //dispatch(showBookingToast({ open: true, message: "Loading..." }));
            if (data.originGooglePlaceId) {
                await _setFromAddress({ originGooglePlaceId: data.originGooglePlaceId, primaryName: data.originName, streetAddress: data.originStreetAddress.replace(/.$/g, ""), suburb: data.originSuburb, state: data.originState, postcode: data.originPostcode });
            } else if (data.originCustomerLocationId) {
                await _setFromAddress({ originCustomerLocationId: data.originCustomerLocationId });
            }
            if (data.destinationGooglePlaceId) {
                await _setToAddress({ destinationGooglePlaceId: data.destinationGooglePlaceId, primaryName: data.destinationName, streetAddress: data.destinationStreetAddress.replace(/.$/g, ""), suburb: data.destinationSuburb, state: data.destinationState, postcode: data.destinationPostcode });
            } else if (data.destinationCustomerLocationId) {
                await _setToAddress({ destinationCustomerLocationId: data.destinationCustomerLocationId });
            }

            if (!localStorage.getItem("specialNeedsMap")) {
                await _loadSpecialNeeds();
            }
            //dispatch(showBookingToast({ open: false, message: "" }));
        } catch (e) {
            // dispatch(showBookingToast({ open: false, message: "" }));
        }
    };

    const getAge = (transferData: any): number => {
        if (transferData.bYear) {
            return _calculateAge(dateUtil.parse(`${transferData.bYear}-${transferData.bMonth}-${transferData.bDay}`, "yyyy-MM-dd"));
        } else if (transferData.byear) {
            return _calculateAge(dateUtil.parse(`${transferData.byear}-${transferData.bmonth}-${transferData.bday}`, "yyyy-MM-dd"));
        } else {
            return NaN;
        }
    };

    const birthdayFormat = (birthday: string): string => {
        return format(new Date(birthday), "dd/MM/yyyy");
        // return birthday.split('T')[0].replaceAll('-', '/');
    };

    const medicalConditionsUnfiltered = data.MedicalConditions || data.medicalConditions || [];
    const medicalConditions = medicalConditionsUnfiltered.filter((o: any) => o.dateAndTime);

    return isDataExist == false ? (
        <div style={{ width: "100%", marginBottom: 20, overflow: "hidden" }}>
            <Grid container spacing={0}>
                <Grid item xs={6} spacing={3}>
                    <Container style={{ width: "100%" }} maxWidth={false} disableGutters>
                        <ListItemText
                            classes={{ primary: classes.listItemTextPrimary, secondary: classes.listItemTextSecondary }}
                            primary={(data.firstname || data.firstName) + " " + (data.lastname || data.lastName)}
                            secondary={`${isNaN(getAge(data)) ? "" : `${getAge(data)} year old`} ${data.gender == 3 ? "gender not disclosed" : String(data.genderName).toLowerCase()}`}
                        />

                        <div style={{ display: "flex", alignItems: "center", marginBottom: 20, marginTop: 20 }}>
                            <span style={{ fontWeight: 700, marginRight: 10, fontSize: 18, color: colorPrimary }}>DOB</span>
                            <span style={{ fontWeight: 700, marginRight: 10, fontSize: 18, color: colorPrimary }}>{birthdayFormat(data.birthday)}</span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", marginBottom: 20, marginTop: 20 }}>
                            <span style={{ fontWeight: 700, marginRight: 10, fontSize: 18, color: colorPrimary }}>Address</span>
                            <span style={{ fontWeight: 700, marginRight: 10, fontSize: 18, color: colorPrimary }}>{data.patientAddress}</span>
                        </div>

                        {!_.isEmpty(data.mrn) && <div style={{ display: "flex", alignItems: "center", marginBottom: 20, marginTop: 20 }}>
                            <span style={{ fontWeight: 700, marginRight: 10, fontSize: 18, color: colorPrimary }}>MRN</span>
                            <span style={{ fontWeight: 700, marginRight: 10, fontSize: 18, color: colorPrimary }}>{data.mrn}</span>
                        </div>}

                        <div style={{ display: "flex", alignItems: "center", marginBottom: 40, marginTop: 20 }}>
                            <span style={{ fontWeight: 700, marginRight: 10, fontSize: 18, color: colorPrimary }}>Priority</span>
                            <span style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "1rem", height: "1rem", borderRadius: 3, fontSize: 14, padding: 4, color: colorPrimary, background: theme == "dark" ? "#FFFFFF40" : "rgb(243, 243, 243)" }}>
                                {data.Priority || data.priority || 0}
                            </span>

                            {data.returnTrip && <RepeatIcon style={{ marginLeft: 20, marginRight: 10, height: 19, color: colorPrimary }} />}
                            {data.returnTrip && <span>Return trip</span>}
                        </div>

                        <div style={{ width: "90%" }}>
                            <SimpleCard note={data.originNote} addressBackground={addressBackground} colorPrimary={colorPrimary} heading="FROM" scheduledDate={data.Date || data.scheduledDate ? "Scheduled Pickup: " + String(data.scheduledDate).split("T")[1].substr(0, 5) : null} {...fromAddress} />

                            <SimpleCard note={data.destinationNote} colorPrimary={colorPrimary} addressBackground={addressBackground} heading="TO" {...toAddress} />
                        </div>
                    </Container>
                </Grid>
                <Grid item xs={6} spacing={3}>
                    <Container style={{ width: "100%" }} maxWidth={false} disableGutters>
                        <div>
                            <List
                                subheader={
                                    <ListSubheader component="div" style={{ fontWeight: 700, fontSize: 18, color: colorPrimary, marginTop: 15, marginBottom: 10, opacity: 0.28 }}>
                                        NEEDS
                                    </ListSubheader>
                                }
                                dense={true}
                            >
                                {data.mobility === 1 ? (
                                    <NeedListItem theme={theme} colorPrimary={colorPrimary} key="notambulant" text={"Not Ambulant"} iconUrl={"../../assets/svgs/nonambulant.svg"} />
                                ) : (
                                    <NeedListItem theme={theme} colorPrimary={colorPrimary} key="ambulant" text={"Ambulant"} iconUrl={"../../assets/svgs/ambulant.svg"} />
                                )}
                                {data.classification ? <AcuityNeedItem theme={theme} colorPrimary={colorPrimary} key="acuity" text={data.classification} /> : null}
                                {(() => {
                                    const specialNeeds = localStorage.getItem("specialNeedsMap");
                                    const specialNeedsMap = JSON.parse(specialNeeds ? specialNeeds : "{}");
                                    if (data.BookingSpecialNeeds) {
                                        return data.BookingSpecialNeeds.map((item: any) => <NeedListItem theme={theme} colorPrimary={colorPrimary} key={item.SpecialNeedId} text={specialNeedsMap[item.SpecialNeedId].name} iconUrl={specialNeedsMap[item.SpecialNeedId]?.icon} />);
                                    } else if (data.bookingSpecialNeeds && data.bookingSpecialNeeds.length > 0) {
                                        return data.bookingSpecialNeeds.map((item: any) => <NeedListItem theme={theme} colorPrimary={colorPrimary} key={item.specialNeedId} text={specialNeedsMap[item.specialNeedId]?.name} iconUrl={specialNeedsMap[item.specialNeedId]?.icon} />);
                                    }
                                })()}
                            </List>
                            <div className={classes.extraInfoContainer}>
                                <div className={classes.extraInfoHeader}>WEIGHT</div>
                                {!data.weight ? (
                                    <div className={classes.extraInfoText}>None Reported</div>
                                ) : (
                                    <div className={classes.extraInfoText}>
                                        {data.weight}KG {data.estimateWeight && "(Estimated)"}
                                    </div>
                                )}
                                {data.weight < 3 || data.weight > 200 || data.GirthIsOver90CM === true ? <div style={{ fontSize: 18, color: "#E15152", marginTop: 10 }}>Patient dimensions outside common parameters</div> : null}
                            </div>

                            {/* MEDICATIONS INFO */}
                            <div className={classes.extraInfoContainer}>
                                <div className={classes.extraInfoHeader}>MEDICATIONS</div>
                                {medicalConditions.map(({ name, dose, unit, route, dateAndTime, brandId, monographId, monographType }: any) => {
                                    return (
                                        <div style={{ flexDirection: "row", alignItems: "center", marginTop: 2, marginBottom: 2, maxWidth: 500 }}>
                                            <span className={classes.extraInfoDateTime}>{dateAndTime ? dateUtil.format(dateUtil.parse(dateAndTime, "yyyy-MM-dd'T'HH:mm:ss"), "dd/MM/yyyy HH:mm") : ""}</span>
                                            <span
                                                className={clsx(classes.medicationsInfo, monographId && monographType && classes.medicationsPopupLink)}
                                                onClick={() => {
                                                  if (monographId && monographType) {
                                                    setModalMedicationType({
                                                        brandId,
                                                        monographId,
                                                        monographType,
                                                    });
                                                    setModalMedicationName(name);
                                                    setIsSnowMedicationModal(true);
                                                  }
                                                }}
                                            >
                                                {name}
                                                {dose ? " " + dose + (unit !== null && unit !== undefined && unit !== "" && medicationUnits.some((u) => u.value == unit) ? medicationUnits.find((u) => u.value == unit)?.text : "") : ""}
                                                {route && medicationRoutes.some((r) => r.value == route) ? " via " + medicationRoutes.find((r) => r.value == route)?.text : ""}
                                            </span>
                                        </div>
                                    );
                                })}
                                {medicalConditions.length === 0 && <div className={classes.extraInfoText}>None Reported</div>}
                            </div>

                            <div className={classes.extraInfoContainer}>
                                <div className={classes.extraInfoHeader}>CONDITIONS</div>
                                <div className={classes.extraInfoText}>{data.patientMedicalConditions || data.PatientMedicalConditions || "None Reported"}</div>
                            </div>
                            <div className={classes.extraInfoContainer}>
                                <div className={classes.extraInfoHeader}>ALLERGIES</div>
                                <div className={classes.extraInfoText}>{data.PatientAllergies || data.patientAllergies || "None Reported"}</div>
                            </div>
                            <div className={classes.extraInfoContainer}>
                                <div className={classes.extraInfoHeader}>OBSERVATIONS</div>
                                {(data.Observations &&
                                    data.Observations.filter((o: any) => o.dateAndTimel).map((item: any) => {
                                        const { dateAndTime, capRefill, pain, pulse, respRate, sP02, temp, avpu, bpSystolic, bpDiastolic, bgl } = item;
                                        return (
                                            <div style={{ flexDirection: "row", alignItems: "center", marginTop: 10, marginBottom: 20, maxWidth: 500 }}>
                                                <span className={classes.extraInfoDateTime}>{dateAndTime ? dateUtil.format(dateUtil.parse(dateAndTime, "yyyy-MM-dd'T'HH:mm:ss"), "dd/MM/yyyy'T'HH:mm") : ""}</span>
                                                <span style={{ fontSize: 21, fontWeight: 400, color: colorPrimary, height: "100%" }}>
                                                    {Object.keys(item)
                                                        .map(function (k) {
                                                            if (k == "respRate" && item["respRate"]) return "Resp " + item.respRate;
                                                            if (k == "pulse" && item["pulse"]) return item.pulse + "bpm";
                                                            if (k == "bpSystolic" && item["bpSystolic"]) return "BP " + item.bpSystolic;
                                                            if (k == "bpDiastolic" && item["bpDiastolic"]) return item["bpSystolic"] ? "/" + item.bpDiastolic : "BP /" + item.bpDiastolic;
                                                            if (k == "temp" && item["temp"]) return "Temp " + item.temp + "C";
                                                            if (k == "sP02" && item["sP02"]) return "SpO2 " + item.sP02 + "%";
                                                            if (k == "pain" && item.pain>=0 && item["pain"] != null) return "Pain " + item.pain;
                                                            if (k == "avpu" && item["avpu"]) return "AVPU " + item.avpu;
                                                            if (k == "capRefill" && item["capRefill"]) return "Cap Refill " + item.capRefill + "s";
                                                            if (k == "bgl" && item["bgl"]) return "BGL " + item.bgl;
                                                        })
                                                        .filter((s) => s)
                                                        .join(", ")
                                                        .replace(", /", "/")}
                                                </span>
                                            </div>
                                        );
                                    })) ||
                                    (data.observations &&
                                        data.observations
                                            .filter((o: any) => o.dateAndTime)
                                            .map((item: any) => {
                                                const { dateAndTime } = item;
                                                return (
                                                    <div style={{ flexDirection: "row", alignItems: "center", marginTop: 10, marginBottom: 20, maxWidth: 500 }}>
                                                        <span className={classes.extraInfoDateTime}>{dateAndTime ? dateUtil.format(dateUtil.parse(dateAndTime, "yyyy-MM-dd'T'HH:mm:ss"), "dd/MM/yyyy HH:mm") : ""}</span>
                                                        <span style={{ fontSize: 21, fontWeight: 400, color: colorPrimary, height: "100%" }}>
                                                            {Object.keys(item)
                                                                .map(function (k) {
                                                                    if (k == "respRate" && item["respRate"]) return "Resp " + item.respRate;
                                                                    if (k == "pulse" && item["pulse"]) return item.pulse + "bpm";
                                                                    if (k == "bpSystolic" && item["bpSystolic"]) return "BP " + item.bpSystolic;
                                                                    if (k == "bpDiastolic" && item["bpDiastolic"]) return item["bpSystolic"] ? "/" + item.bpDiastolic : "BP /" + item.bpDiastolic;
                                                                    if (k == "temp" && item["temp"]) return "Temp " + item.temp + "C";
                                                                    if (k == "sP02" && item["sP02"]) return "SpO2 " + item.sP02 + "%";
                                                                    if (k == "pain" && item.pain>=0 && item["pain"] != null) return "Pain " + item.pain;
                                                                    if (k == "avpu" && item["avpu"]) return "AVPU " + item.avpu;
                                                                    if (k == "capRefill" && item["capRefill"]) return "Cap Refill " + item.capRefill + "s";
                                                                    if (k == "bgl" && item["bgl"]) return "BGL " + item.bgl;
                                                                })
                                                                .filter((s) => s)
                                                                .join(", ")
                                                                .replace(", /", "/")}
                                                        </span>
                                                    </div>
                                                );
                                            }))}
                                {(!data.Observations || data.Observations.filter((o: any) => o.dateAndTime).length == 0) && (!data.observations || data.observations.filter((o: any) => o.dateAndTime).length == 0) ? <div className={classes.extraInfoText}>None Reported</div> : ""}
                            </div>
                            {data.alteredCallingCriteria === false && data.advancedCareDirective === false && data.observationsStatus === true ? (
                                <li style={{ fontSize: 18, color: "#E15152", marginLeft: 100 }}>
                                    <span style={{ position: "relative", left: -10 }}>Patient has no Altered Calling Criteria</span>
                                </li>
                            ) : null}
                            {data.observationsStatus === true ? (
                                <li style={{ fontSize: 18, color: "#E15152", marginLeft: 100 }}>
                                    <span style={{ position: "relative", left: -10 }}>Observation out of bounds</span>
                                </li>
                            ) : null}
                        </div>
                    </Container>
                </Grid>
            </Grid>

            {isSnowMedicationModal && (
                <MedicationDetails
                    medicationInfo={modalMedicationType}
                    medicationName={modalMedicationName}
                    onClose={() => {
                        setIsSnowMedicationModal(false);
                    }}
                />
            )}
        </div>
    ) : null;
}

export default React.memo(TransferDetailsComponent);